import classNames from "classnames";
import { cva } from "cva";
import { ChangeEvent, InputHTMLAttributes, ReactNode, useRef } from "react";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { Button, FontAwesomeIcon, Label, QuestionIcon } from "..";
import getFileSize from "utils/getFileSize";
import checkIfValidFormat from "utils/checkIfValidFormat";
import { useTranslation } from "react-i18next";
import openToast from "utils/openToast";

export type BaseProps = {
  name?: string;
  value?: string;
  label?: string;
  placeholder?: ReactNode | string;
  inputClassName?: string;
  tooltip?: string;
  error?: boolean;
  locked?: boolean;
  isLoading?: boolean;
  onSave?: Function;
  formats?: string[];
  maxSize: number;
  buttonText?: string;
  customRef?: any;
  hideSaveButton?: boolean;
};

type Props = BaseProps &
  Omit<InputHTMLAttributes<HTMLInputElement>, keyof BaseProps | "type">;

const FileInputStyles = cva(
  "w-full px-3 py-2.5 leading-6 focus:outline-none focus:ring-0 border bg-inherit",
  {
    variants: {
      state: {
        default: [
          "-bg--theme-colors-action-tertiary-default-bg -text--theme-colors-action-tertiary-default-fg -border--theme-colors-action-tertiary-default-border",
          "disabled:-border--theme-colors-action-tertiary-disabled-fg disabled:-border--theme-colors-action-tertiary-disabled-border -bg--theme-colors-action-tertiary-disabled-bg",
          "hover:-text--theme-colors-action-tertiary-hover-fg -bg--theme-colors-action-tertiary-hover-bg hover:-border--theme-colors-action-tertiary-hover-border",
          "focus:-border--theme-colors-action-tertiary-focused-border focus:-text--theme-colors-action-tertiary-focused-fg focus:-bg--theme-colors-action-tertiary-focused-bg  -rounded-input",
        ],
        error:
          "-border--component-colors-field-error-input-border -text--component-colors-field-error-input-text placeholder:-text--component-colors-field-error-input-placeholder",
        locked:
          "-border--component-colors-field-locked-input-border -text--component-colors-field-locked-input-text placeholder:-text--component-colors-field-locked-input-placeholder",
      },
    },
    defaultVariants: {
      state: "default",
    },
  },
);

const FileInput = (props: Props) => {
  const { t } = useTranslation();
  const {
    placeholder,
    className,
    inputClassName,
    label,
    error,
    locked,
    formats,
    onSave,
    hideSaveButton,
    maxSize = 2,
    onChange,
    buttonText,
    customRef,
    ...rest
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const isEmpty = inputRef.current?.files?.length === 0;

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const isAllowed = checkIfValidFormat(file, formats || []);

      if (!isAllowed) {
        if (inputRef.current) inputRef.current.value = "";
        return;
      }
      const fileSize = parseFloat(getFileSize(file.size));
      if (fileSize > maxSize) {
        if (inputRef.current) inputRef.current.value = "";
        return openToast(`File size must be less than ${maxSize}MB`, "error");
      }
      if (onChange) {
        onChange(e);
      }
    }
  };
  return (
    <div className={classNames("w-full flex flex-col", className)}>
      <div className="w-full flex items-center justify-between -border--theme-colors-action-tertiary-focused-border">
        <Label
          locked={locked}
          error={error}
          htmlFor={props.name}
          required={props.required}
        >
          {label}
        </Label>
        {props.tooltip && (
          <div className="flex items-center justify-center cursor-pointer">
            <span
              data-tooltip-id={`${label}-${"file"}`}
              data-tooltip-content={props.tooltip}
            >
              <QuestionIcon width={20} height={20} />
            </span>
            <Tooltip
              place={"right"}
              className="bg-gray-800 max-w-[180px] text-xs text-white leading-6 p-1"
              id={`${label}-${"file"}`}
            />
          </div>
        )}
      </div>
      {
        <div
          className={classNames("flex flex-col md:flex-row gap-y-2.5 gap-x-6", {
            "mt-2.5": label,
          })}
        >
          <div
            onClick={() => {
              inputRef.current?.click();
            }}
            className={classNames(
              FileInputStyles({
                state: (error && "error") || (locked && "locked") || "default",
              }),
              inputClassName,
              "cursor-pointer h-full pe-20",
              "truncate",
              { "cursor-not-allowed": props.disabled },
            )}
          >
            {isEmpty
              ? placeholder || t("fileInput.selectPlaceholder")
              : (inputRef.current?.files?.[0] || customRef?.current?.files?.[0])
                  ?.name || t("fileInput.noFileSelected")}
          </div>
          <input
            ref={customRef ? customRef : inputRef}
            {...rest}
            type={"file"}
            className="hidden"
            accept={`${formats?.map((f) => `,${f}`).join("")}`}
            onChange={handleOnChange}
          />
          {!hideSaveButton && (
            <Button
              disabled={props.disabled}
              isLoading={props.isLoading}
              className="!min-w-[140px] shrink-0"
              variant={"primary"}
              size={"md"}
              onClick={(e) =>
                isEmpty ? inputRef.current?.click() : onSave?.(e)
              }
            >
              <div className="flex items-center gap-x-2 whitespace-nowrap">
                {isEmpty
                  ? t("fileInput.browsePhotoLabel")
                  : buttonText || t("fileInput.saveLabel")}
                <FontAwesomeIcon name="fa-cloud-arrow-up" size="lg" />
              </div>
            </Button>
          )}
        </div>
      }
    </div>
  );
};

export default FileInput;
